import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { GeneralParameterEnums } from 'src/app/enum/general-parameter-enums';
import { OptionWindow } from 'src/app/enum/option-window-enums';
import { ProfileUserEnums } from 'src/app/enum/profile-user-enums';
import { WindowEnums } from 'src/app/enum/window-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { CompanyModel } from 'src/app/models/management/company.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InventoryOutputDetailTableModel } from 'src/app/models/tables/inventory/inventory-output-detail-table.model';
import { InventoryOutputTableModel } from 'src/app/models/tables/inventory/inventory-output-table.model';
import { PendingOrderTableModel } from 'src/app/models/tables/shopping/pending-order-table.model';
import { OptionWindowService } from 'src/app/services/authentication/option-window.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InventoryOutputDetailService } from 'src/app/services/inventory/inventory-output-detail.service';
import { InventoryOutputService } from 'src/app/services/inventory/inventory-output.service';
import { CompanyService } from 'src/app/services/management/company.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-inventory-output',
  templateUrl: './inventory-output.component.html',
  styleUrls: ['./inventory-output.component.scss'],
  standalone: false
})
export class InventoryOutputComponent implements OnInit, OnDestroy {

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  company: CompanyModel = new CompanyModel();
  detailData: PagedResponseModel<InventoryOutputDetailTableModel> = new PagedResponseModel<InventoryOutputDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<InventoryOutputTableModel> = new PagedResponseModel<InventoryOutputTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined;
  limit: number = 10;
  nameWindow: string = 'Salidas de Inventario';
  page: number = 1;
  pendingOrderData: PagedResponseModel<PendingOrderTableModel> = new PagedResponseModel<PendingOrderTableModel>();
  pendingOrderHeader: ColumnResponseModel[] = [];
  permissionUpdatable: boolean = false;
  permissionsUser: Array<OptionWindowModel> = new Array<OptionWindowModel>();
  projectName = ProjectName.projectName;
  swModuloCompra: boolean = false;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
  ];
  url: string = "";
  userAuthenticated: UserModel = new UserModel();

  constructor(private companyService: CompanyService,
    private headerService: HeaderService,
    private inventoryOutputDetailService: InventoryOutputDetailService,
    private inventoryOutputService: InventoryOutputService,
    private optionWindowService: OptionWindowService,
    private router: Router,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    private windowService: WindowService) {
    this.getPermission();
  }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem('inventory-output');
    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getInventoryOutputsWithPagination(),
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  createNewRegister() {
    if (this.permissionUpdatable) {
      this.router.navigate(['/inventory-output/register']);
    } else {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para crear salidas de inventario. Comuníquese con el administrador del sistema.'
      );
    }
  }

  async exportDetailsConsultation() {
    if (this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador || this.permissionsUser.find(x => x.code == OptionWindow.Exportar)) {
      try {
        const response = await firstValueFrom(this.inventoryOutputDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Salidas de Inventario Detalles.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el detalle de las salidas de inventario.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para exportar detalle de salidas de inventario, comuniquese con el administrador del sistema.'
      );
    }
  }

  async exportGeneralConsultation() {
    if (this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador || this.permissionsUser.find(x => x.code == OptionWindow.Exportar)) {
      try {
        const response = await firstValueFrom(this.inventoryOutputService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Salidas de Inventario.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar salidas de inventario.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para exportar salidas de inventario, comuniquese con el administrador del sistema.'
      );
    }
  }

  async exportPendingOrdersConsultation() {
    if (this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador || this.permissionsUser.find(x => x.code == OptionWindow.Exportar)) {
      try {
        const response = await firstValueFrom(this.inventoryOutputDetailService.exportPendingOrderConsultation(this.filters, this.columnSort, this.ascendant));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Pedidos Pendientes.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar pedidos pendientes.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para exportar pedidos pendientes de salidas de inventario, comuniquese con el administrador del sistema.'
      );
    }
  }

  async getColumnsForDetailsConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.inventoryOutputDetailService.getColumnsForDetailConsultation());
      }
    } catch (error) {
      console.error('Error al buscar columnas para la consulta de detalles.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.inventoryOutputService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar columnas para la consulta general.', error);
    }
  }

  async getColumnsForPendingOrdersConsultation() {
    try {
      if (this.pendingOrderHeader.length === 0) {
        this.pendingOrderHeader = await firstValueFrom(this.inventoryOutputDetailService.getColumnsForPendingOrdersConsultation());
      }
    } catch (error) {
      console.error('Error al buscar columnas para la consulta de pedidos pendientes.', error);
    }
  }

  async getDetailsWithPagination() {
    try {
      this.detailData = await firstValueFrom(
        this.inventoryOutputDetailService.getAllInventoryOutputDetailsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters)
      );
    } catch (error) {
      console.error('Error al buscar los detalles de las salidas de inventario.', error);
    }
  }

  getGeneralParametersByCompany() {
    return new Promise((resolve) => {
      this.userAuthenticatedService.generalParameterObservable.subscribe(response => {
        this.swModuloCompra = response.some(x => x.generalParameterId == GeneralParameterEnums.moduloCompras);
      });
      resolve(true);
    });
  }

  async getPendingOrdersWithPagination() {
    try {
      this.pendingOrderData = await firstValueFrom(
        this.inventoryOutputDetailService.getAllPendingOrdersWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters)
      );
    } catch (error) {
      console.error('Error al buscar los pedidos pendientes de las salidas de inventario.', error);
    }
  }

  async getPermission() {
    let url = this.router.url as string
    url = url.substring(1);
    this.url = url;
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    try {
      this.company = await firstValueFrom(this.companyService.getCompanyByUserAutheticated());
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al obtener el perfil de permisos por URL.', error);
    }
    this.getPermissionsForUserByWindow();
    this.getGeneralParametersByCompany();
  }

  async getPermissionsForUserByWindow() {
    this.userAuthenticated = await firstValueFrom(this.userAuthenticatedService.userObservable.pipe(filter(user => user && user.userId !== 0)));
    this.permissionsUser = await firstValueFrom(this.optionWindowService.getPermissionsForUserByWindow(WindowEnums.InventoryOutput));
  }

  async getInventoryOutputsWithPagination() {
    try {
      this.generalData = await firstValueFrom(
        this.inventoryOutputService.getAllInventoryOutputsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters)
      );
    } catch (error) {
      console.error('Error al buscar las salidas de inventario.', error);
    }
  }

  goToHome(event: Event) {
    event.preventDefault();
    this.router.navigate(['/main']);
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;
    this.pendingOrderHeader.length = this.pendingOrderHeader.length === 1 ? 0 : this.pendingOrderHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getInventoryOutputsWithPagination()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailsConsultation(),
          this.getDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 2:
        await Promise.all([
          this.getColumnsForPendingOrdersConsultation(),
          this.getPendingOrdersWithPagination()
        ]);
        this.pendingOrderData = {
          ...this.pendingOrderData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  onAction(event: { action: string, item?: any, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) { this.router.navigate([`/inventory-output/register/${event.item.id}`]); }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailsConsultation();
        break;
      case 2:
        await this.exportPendingOrdersConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getInventoryOutputsWithPagination()
        break;
      case 1:
        await this.getDetailsWithPagination()
        break;
      case 2:
        await this.getPendingOrdersWithPagination()
        break;
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  reloadWindow(event: Event) {
    event.preventDefault();
    this.router.navigate(['/inventory-output']);
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

}
